/* ===================================================================
   useTokenSync() -- keep React Query honest about the active rr_ token.

   THE COLD-RELOAD RACE (root cause):
   On a hard refresh, _tokenCache is seeded synchronously from localStorage,
   so the data hooks see hasToken=true on first paint and fire immediately --
   with whatever rr_ token was persisted. Meanwhile useSessionToken waits for
   the async Supabase getSession(), then exchanges the user JWT for a FRESH
   rr_ token via setToken(). Two failure shapes followed:

     1. The persisted token is empty/expired -> the first fetch 401s (or the
        query was disabled), the panel shows empty state, and because the
        queryKey never changed, React Query did NOT refetch when the fresh
        token landed a tick later. Only navigating away + back (a remount)
        re-ran the query with the good token. That is exactly the reported
        "empty until you navigate away and back" glitch.
     2. Even on a clean first fetch, swapping to a newly-minted key left any
        in-flight/disabled query stale.

   THE FIX:
   Subscribe to the reactive token store (useApiToken via useSyncExternalStore)
   and, whenever the token transitions to a NEW non-null value, invalidate all
   queries. Invalidation refetches active queries and marks the rest stale, so:
     - a query that fired with a stale/missing token refetches with the fresh
       one and the panel populates -- no navigate-away needed;
     - a query that was disabled (no token) and just became enabled is already
       handled by React Query's enable->fetch, and the invalidate is a no-op
       harmless backstop.

   Mounted ONCE near the top of the app (App.tsx). Pairs with the per-hook
   `enabled: hasToken` gates, which prevent a guaranteed-401 on first paint;
   this hook closes the "fresh token after first fetch" gap they cannot.
   =================================================================== */
import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useApiToken } from './useApiToken';

export function useTokenSync(): void {
  const token = useApiToken();
  const qc = useQueryClient();
  // The token value we last reconciled against. Starts at the synchronously
  // seeded value so the very first paint (token already in localStorage) does
  // NOT trigger a redundant invalidate -- the gated queries fetch normally.
  const lastRef = useRef<string | null>(token);

  useEffect(() => {
    if (token === lastRef.current) return;
    lastRef.current = token;
    // Only a real (non-null) token swap warrants a refetch. On sign-out the
    // token goes null; the queries disable themselves via their enabled gates,
    // so there is nothing to refetch (and we avoid a sign-out refetch storm).
    if (token) {
      void qc.invalidateQueries();
    }
  }, [token, qc]);
}
